import { useState } from "react";
import { Download, FileSignature, PenLine, Upload, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, API, formatErr } from "@/lib/api";
import { trackEvent } from "@/lib/tracking";

const Uploader = ({ token, mode, accept, onDone }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const send = async () => {
    if (!file) return setErr("Seleziona un file.");
    setErr("");
    setLoading(true);
    const fd = new FormData();
    fd.append("mode", mode);
    fd.append("file", file);
    try {
      await api.post(`/public/mandate/${token}/upload`, fd);
      trackEvent("mandate_signed", { mode });
      onDone();
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-5 space-y-3">
      <label className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-lg p-4 cursor-pointer hover:border-navy transition-colors">
        <Upload className="h-5 w-5 text-slate-500" />
        <span className="text-sm text-slate-600 truncate">{file ? file.name : `Scegli file (${accept})`}</span>
        <input data-testid={`mandate-upload-${mode}-input`} type="file" accept={accept} className="hidden" onChange={(e) => setFile(e.target.files[0])} />
      </label>
      {err && <p data-testid={`mandate-upload-${mode}-error`} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
      <Button data-testid={`mandate-upload-${mode}-btn`} onClick={send} disabled={loading} className="rounded-full bg-navy hover:bg-navy-800">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia mandato firmato"}
      </Button>
    </div>
  );
};

export const MandateSign = ({ token, info, onDone, onEdit }) => {
  const [mode, setMode] = useState(null);
  const s = info.settings;
  const opt = (m, Icon, t, sub) => (
    <button type="button" data-testid={`mandate-${m}-opt`} onClick={() => setMode(m)}
      className={`text-left border rounded-xl p-5 transition-all ${mode === m ? "border-navy ring-2 ring-navy/10 bg-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
      <Icon className="h-6 w-6 text-gold-dark" strokeWidth={1.5} />
      <p className="font-serif text-2xl text-navy mt-3">{t}</p>
      <p className="text-sm text-slate-600 mt-1">{sub}</p>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="mandate-sign-step">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div><p className="font-semibold text-navy">Il tuo mandato è pronto</p><p className="text-sm text-slate-500">Scaricalo, verificane i dati e firmalo.</p></div>
        <div className="flex gap-2">
          <Button variant="ghost" data-testid="mandate-edit-data-btn" onClick={onEdit}>Modifica dati</Button>
          <Button asChild data-testid="mandate-download-pdf-btn" className="rounded-full bg-gold text-navy hover:bg-gold-dark">
            <a href={`${API}/public/mandate/${token}/pdf`}><Download className="h-4 w-4 mr-2" />Scarica PDF</a>
          </Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {opt("digitale", FileSignature, "Firma digitale", "Hai una firma digitale (smart card, token, firma remota)? Carica il PDF firmato PAdES o il file .p7m CAdES.")}
        {opt("analogico", PenLine, "Firma su carta", "Stampa il mandato, firmalo a mano in tutti gli spazi indicati e inviaci la scansione.")}
      </div>
      {mode === "digitale" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="mandate-digital-panel">
          <p className="text-sm text-slate-600">Verificheremo automaticamente integrità e validità crittografica della firma. Formati: <span className="font-mono">.pdf</span> (PAdES) o <span className="font-mono">.p7m</span> (CAdES).</p>
          <Uploader token={token} mode="digitale" accept=".pdf,.p7m" onDone={onDone} />
        </div>
      )}
      {mode === "analogico" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="mandate-analog-panel">
          <ol className="text-sm text-slate-700 space-y-2 list-decimal pl-5">
            <li>Scarica e stampa il PDF del mandato.</li>
            <li>Indica luogo e data e firma negli spazi "Il Mandante" (incluse le clausole ex artt. 1341-1342 c.c.).</li>
            <li>Scansiona o fotografa tutte le pagine in modo leggibile.</li>
            <li>Carica qui la scansione, oppure inviala via email allegando copia del documento d'identità.</li>
          </ol>
          <p className="flex items-center gap-2 text-sm mt-4 text-navy"><Mail className="h-4 w-4" /> <a href={`mailto:${s.email_ricezione_mandati}`} data-testid="mandate-analog-email-link" className="underline">{s.email_ricezione_mandati}</a></p>
          <p className="text-xs text-slate-500 mt-2">Lo studio potrà richiederti l'originale cartaceo per posta.</p>
          <Uploader token={token} mode="analogico" accept=".pdf,.jpg,.jpeg,.png" onDone={onDone} />
        </div>
      )}
    </div>
  );
};
